"use client";

import posthog from "posthog-js";

import { useEffect } from "react";

import { Button } from "@/components/ui";
import AIBadge from "@/components/ui/ai-badge";

import { logErrorToBackend } from "@/lib/error-logger";

// Function to detect chunk loading errors
function isChunkLoadError(error: Error): boolean {
  return (
    error.name === "ChunkLoadError" ||
    (error.message?.includes("Loading chunk") ?? false) ||
    (error.message?.includes("ChunkLoadError") ?? false) ||
    // Also catch network errors that might be related to missing chunks
    ((error.message?.includes("Failed to fetch") ?? false) &&
      ((error.stack?.includes("static/chunks") ?? false) ||
        (error.stack?.includes("_next") ?? false)))
  );
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    logErrorToBackend(error, "global_error_boundary");

    posthog.captureException(error);

    if (isChunkLoadError(error)) {
      console.log("Chunk loading error detected, refreshing page...", error);
      window.location.reload();
      return;
    }
  }, [error]);

  // If it's a chunk loading error, show a minimal loading state while refreshing
  if (isChunkLoadError(error)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background">
        <div className="max-w-md rounded-lg border border-border p-6 text-center">
          <div className="mb-4 flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-t-accent-600"></div>
          </div>
          <p className="text-foreground-secondary">
            Updating to the latest version...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <div className="max-w-md rounded-lg border border-border p-6">
        <h2 className="mb-4 flex items-center gap-2 text-h1 font-medium">
          <AIBadge size={24} />
          Something went wrong.
        </h2>

        <p className="mb-6 text-foreground-secondary">
          Decoda&apos;s engineering team has been notified and will fix this
          issue as soon as possible.
        </p>
        <Button variant="secondary" onClick={() => reset()}>
          Refresh the Page
        </Button>
      </div>
    </div>
  );
}
